'use client'

import Image from 'next/image'
import { useInstanceContext } from '@/lib/context/InstanceProvider'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth/AuthProvider'

// Enhanced token balance type for instance-specific data
interface InstanceTokenData {
  instanceTokens: number
  tokenType: 'nt'
  tokenLabel: string
  prizeTokens: number
}

export default function InstanceAwareTokenDisplay() {
  const { currentInstance } = useInstanceContext()
  const { user } = useAuth()

  // Fetch instance-specific token data.
  const { data: tokenData, isLoading } = useQuery({
    queryKey: ['instanceTokens', currentInstance?.id, user?.id],
    queryFn: async (): Promise<InstanceTokenData> => {
      if (!user?.id || !currentInstance) {
        return {
          instanceTokens: 0,
          tokenType: 'nt',
          tokenLabel: 'NT',
          prizeTokens: 0
        }
      }

      return {
        instanceTokens: currentInstance.nt_token_balance,
        tokenType: 'nt',
        tokenLabel: 'NT',
        prizeTokens: 0
      }
    },
    enabled: !!user?.id && !!currentInstance,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000 // Refetch every 30 seconds for real-time updates
  })

  if (isLoading || !currentInstance) {
    return (
      <div className="flex min-w-0 items-center gap-1 lg:gap-[6px]">
        <div className="h-5 w-5 rounded-full bg-neu-surface-recessed animate-pulse lg:h-[25px] lg:w-[25px]" />
        <div className="h-5 w-12 rounded bg-neu-surface-recessed animate-pulse lg:h-[24px] lg:w-[60px]" />
      </div>
    )
  }

  const { instanceTokens, tokenLabel } = tokenData || {}

  return (
    <div className="flex min-w-0 items-center gap-1 lg:gap-[6px]">
      {/* Instance-specific tokens */}
      <div className="relative h-5 w-5 flex-none lg:h-[25px] lg:w-[25px]">
        <Image
          src="/assets/coins/coin.png"
          alt={`${tokenLabel} Token`}
          fill
          sizes="(min-width: 1024px) 25px, 20px"
          className="object-contain"
        />
      </div>

      <span className="min-w-[44px] text-center font-montserrat text-sm font-bold leading-5 tracking-[0.15px] text-neu-text-primary lg:min-w-[60px] lg:text-[16px] lg:leading-[24px]">
        {instanceTokens?.toLocaleString() || '0'}
      </span>
    </div>
  )
}
